'use client';

import {
  ActionSheet,
  type ActionSheetAction,
  type ActionSheetGroup
} from '@components/mobile/overlays/action-sheet';
import { ResponsiveOverlay } from '@components/mobile/overlays/responsive-overlay';
import { Button } from '@components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@components/ui/dropdown-menu';
import { isImageContentType } from '@files/constants';
import { useIsMobile } from '@hooks/use-mobile';
import {
  canCopyImages,
  copyImageFromUrl,
  copyText,
  fileLinkForCopy
} from '@utils/clipboard';
import { cn } from '@utils/cn';
import {
  Copy,
  Download,
  Eye,
  Link2,
  MoreHorizontal,
  Trash2
} from 'lucide-react';
import { useTranslations } from 'next-intl';
import { FC, ReactNode, useState } from 'react';
import { toast } from 'sonner';
import type { AttachmentView } from './AttachmentGallery';

export function downloadUrlFor(file: Pick<AttachmentView, 'url'>): string {
  return `${file.url}${file.url.includes('?') ? '&' : '?'}download=1`;
}

function triggerDownload(file: AttachmentView) {
  const anchor = document.createElement('a');
  anchor.href = downloadUrlFor(file);
  anchor.download = file.filename;
  anchor.rel = 'noreferrer';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
}

type FileActionsMenuProps = {
  file: AttachmentView;
  canDelete?: boolean;
  /** Omit inside the viewer, where the file is already open. */
  onOpen?: () => void;
  onDelete?: (file: AttachmentView) => unknown;
  triggerClassName?: string;
  align?: 'start' | 'end';
};

type Action = {
  id: string;
  label: ReactNode;
  icon: ReactNode;
  destructive?: boolean;
  href?: string;
  onSelect: () => void;
};

/**
 * Slack-style "more actions" for one attachment: DropdownMenu on desktop,
 * ActionSheet on phones. Delete confirms through ResponsiveOverlay.
 */
const FileActionsMenu: FC<FileActionsMenuProps> = ({
  file,
  canDelete = false,
  onOpen,
  onDelete,
  triggerClassName,
  align = 'end'
}) => {
  const t = useTranslations('files');
  const isMobile = useIsMobile();
  const [sheetOpen, setSheetOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const copyLink = async () => {
    if (await copyText(fileLinkForCopy(file.url))) {
      toast.success(t('link-copied'), {
        description: t('link-copied-description')
      });
    } else {
      toast.error(t('copy-failed'));
    }
  };

  const copyImage = async () => {
    if (await copyImageFromUrl(file.url, file.contentType)) {
      toast.success(t('image-copied'));
    } else {
      toast.error(t('copy-failed'));
    }
  };

  const actions: Action[] = [];
  if (onOpen) {
    actions.push({
      id: 'open',
      label: t('open'),
      icon: <Eye />,
      onSelect: onOpen
    });
  }
  actions.push({
    id: 'download',
    label: t('download'),
    icon: <Download />,
    href: downloadUrlFor(file),
    onSelect: () => triggerDownload(file)
  });
  actions.push({
    id: 'copy-link',
    label: t('copy-link'),
    icon: <Link2 />,
    onSelect: () => void copyLink()
  });
  if (isImageContentType(file.contentType) && canCopyImages()) {
    actions.push({
      id: 'copy-image',
      label: t('copy-image'),
      icon: <Copy />,
      onSelect: () => void copyImage()
    });
  }
  const deleteAction: Action | null =
    canDelete && onDelete
      ? {
          id: 'delete',
          label: t('delete'),
          icon: <Trash2 />,
          destructive: true,
          onSelect: () => setConfirmOpen(true)
        }
      : null;

  const trigger = (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      aria-label={t('more-actions', { name: file.filename })}
      data-no-message-menu=""
      className={cn('shrink-0', triggerClassName)}
      onPointerDown={event => event.stopPropagation()}
      onClick={isMobile ? () => setSheetOpen(true) : undefined}
    >
      <MoreHorizontal />
    </Button>
  );

  const confirm = (
    <ResponsiveOverlay
      open={confirmOpen}
      onOpenChange={setConfirmOpen}
      title={t('delete-title')}
      description={t('delete-description', { name: file.filename })}
      footer={
        <>
          <Button
            type="button"
            variant="outline"
            onClick={() => setConfirmOpen(false)}
          >
            {t('cancel')}
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={() => {
              setConfirmOpen(false);
              void onDelete?.(file);
            }}
          >
            {t('delete')}
          </Button>
        </>
      }
    >
      {null}
    </ResponsiveOverlay>
  );

  if (isMobile) {
    const toSheetAction = (action: Action): ActionSheetAction => ({
      id: action.id,
      label: action.label,
      icon: <span className="[&>svg]:size-4">{action.icon}</span>,
      destructive: action.destructive,
      onSelect: action.onSelect
    });
    const groups: ActionSheetGroup[] = [
      { id: 'actions', actions: actions.map(toSheetAction) }
    ];
    if (deleteAction) {
      groups.push({ id: 'danger', actions: [toSheetAction(deleteAction)] });
    }
    return (
      <>
        {trigger}
        <ActionSheet
          open={sheetOpen}
          onOpenChange={setSheetOpen}
          title={file.filename}
          groups={groups}
        />
        {confirm}
      </>
    );
  }

  const itemClassName =
    'min-h-9 cursor-pointer gap-3 rounded-md px-3 text-body-dense';
  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
        <DropdownMenuContent
          align={align}
          sideOffset={6}
          collisionPadding={12}
          className="min-w-48 rounded-xl border-border/80 bg-popover/95 p-1 shadow-none backdrop-blur-2xl"
        >
          {actions.map(action =>
            action.href ? (
              <DropdownMenuItem
                key={action.id}
                asChild
                className={itemClassName}
              >
                <a href={action.href} download={file.filename} rel="noreferrer">
                  {action.icon}
                  <span>{action.label}</span>
                </a>
              </DropdownMenuItem>
            ) : (
              <DropdownMenuItem
                key={action.id}
                className={itemClassName}
                onSelect={action.onSelect}
              >
                {action.icon}
                <span>{action.label}</span>
              </DropdownMenuItem>
            )
          )}
          {deleteAction ? (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                className={cn(
                  itemClassName,
                  'text-destructive focus:text-destructive'
                )}
                onSelect={deleteAction.onSelect}
              >
                {deleteAction.icon}
                <span>{deleteAction.label}</span>
              </DropdownMenuItem>
            </>
          ) : null}
        </DropdownMenuContent>
      </DropdownMenu>
      {confirm}
    </>
  );
};

export default FileActionsMenu;
